/**
 * EmbedChildSectionsManager — manages data source sections owned by a CustomEmbed
 *
 * Child sections are NOT rendered visually — they serve as structured data inputs
 * to the custom code. The SDK exposes methods to read from them.
 *
 * Uses:
 *   - SectionsListManager for Add + list + delete pattern
 *   - SectionRowCard for per-row rendering (same visual as top-level sections)
 *   - FlexibleSectionContentAdapter for content editing inside the collapse
 *
 * @module reactFlow/components/onboarding/editorSectionsV2/EmbedChildSectionsManager
 */

import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import type { LayoutSection } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { FlexibleSectionContentAdapter, buildBindingProxyStep } from './FlexibleSectionContentAdapter';
import { V2VariableBindingSection } from './V2VariableBindingSection';
import { SectionRowCard, getSectionDisplayLabel } from './SectionRowCard';
import { SectionsListManager, type SectionMenuOption } from './SectionsListManager';

// Section kinds that support variable binding (same set as InlineSectionEditor)
const VARIABLE_CAPABLE_KINDS = new Set([
  'SingleSelectionList',
  'MultipleSelectionList',
  'OptionsList',
  'TextField',
]);

// ─── Menu Options (filtered subset of FlexibleLayout section kinds) ─────────

const CHILD_SECTION_OPTIONS: SectionMenuOption[] = [
  { value: 'ProductCatalog', label: 'Product Catalog' },
  { value: 'Button', label: 'Button' },
  { value: 'SingleSelectionList', label: 'Single Selection List' },
  { value: 'MultipleSelectionList', label: 'Multiple Selection List' },
  { value: 'BulletList', label: 'Bullet List' },
  { value: 'TextField', label: 'Text Field' },
  { value: 'Text', label: 'Text' },
  { value: 'Media', label: 'Media (image / video)' },
];

// ─── Props ──────────────────────────────────────────────────────────────────

export interface EmbedChildSectionsManagerProps {
  /** The CustomEmbed section that owns the child sections */
  section: LayoutSection;
  /** Full top-level sections array (needed for writeSections) */
  sections: LayoutSection[];
  /** Write updated sections array */
  writeSections: (updated: LayoutSection[]) => void;
  /** Current editing locale */
  locale: string;
  /** Base step for proxy step construction */
  baseStep: FlowStepNode;
  /** Disables all inputs */
  disabled?: boolean;
  /** Project ID for upload service */
  projectId?: string;
  /** Onboarding ID for upload service */
  onboardingId?: string;
  /** Version ID for upload service */
  versionId?: string;
  /** When true, render V2VariableBindingSection for variable-capable child sections */
  showVariableBinding?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function EmbedChildSectionsManager({
  section,
  sections,
  writeSections,
  locale,
  baseStep,
  disabled,
  projectId,
  onboardingId,
  versionId,
  showVariableBinding = false,
}: EmbedChildSectionsManagerProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const embedContent = (section as any).embedContent as Record<string, unknown> | undefined;
  const childSections = (embedContent?.childSections as LayoutSection[] | undefined) ?? [];

  // Write child sections back to embedContent.childSections
  const writeChildSections = useCallback(
    (updatedChildren: LayoutSection[]) => {
      const updatedSections = sections.map(s => {
        if (s.id !== section.id) return s;
        return {
          ...s,
          embedContent: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(s as any).embedContent,
            childSections: updatedChildren,
          },
        } as unknown as LayoutSection;
      });
      writeSections(updatedSections);
    },
    [sections, section.id, writeSections],
  );

  /** Update a single child section's label */
  const handleLabelChange = useCallback(
    (sectionId: string, newLabel: string) => {
      const updated = childSections.map((s) =>
        s.id === sectionId ? { ...s, label: newLabel } : s,
      );
      writeChildSections(updated);
    },
    [childSections, writeChildSections],
  );

  // No-op onChange for variable binding (writes go through VariableEditorContext, not path-based)
  const bindingOnChange = useCallback((_path: string, _value: unknown) => { /* no-op */ }, []);

  // Build proxy steps for variable-capable child sections.
  // entityId stays as baseStep.id (screen ID); screenType + content from child section.
  const proxyStepMap = useMemo(() => {
    if (!showVariableBinding) return {};
    const map: Record<string, FlowStepNode> = {};
    for (const child of childSections) {
      if (VARIABLE_CAPABLE_KINDS.has(child.kind as string)) {
        map[child.id] = buildBindingProxyStep(baseStep, child);
      }
    }
    return map;
  }, [showVariableBinding, childSections, baseStep]);

  if (childSections.length === 0 && disabled) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <SectionsListManager
        sections={childSections}
        writeSections={writeChildSections}
        locale={locale}
        menuOptions={CHILD_SECTION_OPTIONS}
        title="DATA SOURCES"
        menuTitle="Add data source"
        disabled={disabled}
        renderRow={(child, onDelete) => (
          <SectionRowCard
            key={child.id}
            section={child}
            displayLabel={child.label ?? getSectionDisplayLabel(child)}
            onLabelChange={handleLabelChange}
            onDeleteRequest={(id, label) => onDelete(id, label)}
            disabled={disabled}
            renderExpandedContent={() => (
              <>
                <FlexibleSectionContentAdapter
                  section={child}
                  sections={childSections}
                  writeSections={writeChildSections}
                  locale={locale}
                  baseStep={baseStep}
                  disabled={disabled}
                  projectId={projectId}
                  onboardingId={onboardingId}
                  versionId={versionId}
                />
                {showVariableBinding && proxyStepMap[child.id] && (
                  <V2VariableBindingSection
                    step={proxyStepMap[child.id]}
                    locale={locale}
                    onChange={bindingOnChange}
                    disabled={disabled}
                    defaultExpanded={false}
                    mode="embedded"
                    sectionId={child.id}
                  />
                )}
              </>
            )}
          />
        )}
      />
    </Box>
  );
}
